// Packages
import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';

// Config
import useConfig from '../../config';

// Models
import useModels from '../../models';

// Actions
import useActions from '../../api/actions';

// Components
import useComponents from '../../views/components';

const KeycloakGuard = ({ children }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Config
  const { useKeycloak } = useConfig();
  const { initKeycloakOnLoad, isKeycloakAuthenticated } = useKeycloak();

  // Models
  const { useSelectors } = useModels();
  const { useSelector, useAuthSelectors } = useSelectors();
  const { session } = useAuthSelectors();
  const { isKeycloakAuth, authToken } = useSelector(session);

  // Actions
  const { dispatch } = useActions();

  // Components
  const { useSpinners } = useComponents();
  const { SpinnerLoading } = useSpinners();

  useEffect(() => {
    // If user is not authenticated with Keycloak in Redux, no need to check
    if (!isKeycloakAuth || !authToken) {
      setIsAuthenticated(false);
      setIsChecking(false);
      return;
    }

    // First check if Keycloak instance is already authenticated
    const currentAuth = isKeycloakAuthenticated();

    if (currentAuth) {
      // Already authenticated, allow access
      setIsAuthenticated(true);
      setIsChecking(false);
      return;
    }

    // If not authenticated in the instance, try to restore session
    // This handles page reloads where the instance is reset but token is in Redux
    initKeycloakOnLoad(
      (kc) => {
        // Successfully restored session
        const authenticated = kc?.authenticated || false;
        setIsAuthenticated(authenticated);
        setIsChecking(false);

        // If session restoration failed, clear Redux state
        if (!authenticated) {
          dispatch({ type: 'LOGOUT' });
        }
      },
      (error) => {
        // Failed to restore session, clear Redux state and redirect to login
        console.error('Failed to restore Keycloak session');
        dispatch({ type: 'LOGOUT' });
        setIsAuthenticated(false);
        setIsChecking(false);
      }
    );
  }, [isKeycloakAuth, authToken, isKeycloakAuthenticated, initKeycloakOnLoad, dispatch]);

  if (isChecking) {
    return <SpinnerLoading isOpen={true} text="Verificando autenticación..." />;
  }

  // If user is marked as Keycloak authenticated but session couldn't be restored, redirect to login
  if (isKeycloakAuth && !isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

export default KeycloakGuard;
